import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  AttributeKindEnum,
  MediaAttachmentTypeEnum,
  mediaAttachmentTypeEnum,
} from "@brewbase/db/types";
import UnitSelect from "@/components/unit-select";
import { MeasurementFieldsEditor } from "@/components/template-attribute-item/MeasurementFieldsEditor";
import { InventoryFieldsEditor } from "@/components/template-attribute-item/InventoryFieldsEditor";
import { TextFieldsEditor } from "@/components/template-attribute-item/TextFieldsEditor";
import { FormulaField } from "@/components/formula/FormulaField";
import { FormulaUnitWarning } from "@/components/formula/FormulaUnitWarning";
import type { FormulaContextInput } from "@/components/formula/context";
import InlineEditableInput from "@/components/InlineEditableInput";
import { capitalize } from "typed-case";
import type { BaseTemplateAttribute } from "./types";

type FormulaContextKind = "harvest-attribute" | "test-attribute" | "test-revision-attribute";

// ======================== Section Header ========================

export function SectionHeader({ title }: { title: string }) {
  return (
    <div className="px-3 py-1.5 bg-olive-3 text-xs font-medium text-olive-11 border-y border-olive-6">
      {title}
    </div>
  );
}

// ======================== Flag Box ========================

export function FlagBox({
  id,
  icon,
  label,
  description,
  checked,
  disabled,
  onChange,
  footer,
}: {
  id: string;
  icon: React.ReactNode;
  label: string;
  description: string;
  checked: boolean;
  disabled?: boolean;
  onChange: (checked: boolean) => void;
  footer?: React.ReactNode;
}) {
  return (
    <div className="border bg-muted/20 px-2 py-2">
      <div className="flex items-center gap-2">
        <Checkbox
          id={id}
          checked={checked}
          disabled={disabled}
          onCheckedChange={(c) => onChange(!!c)}
        />
        {icon}
        <Label htmlFor={id} className="text-xs font-medium">
          {label}
        </Label>
      </div>
      <div className="text-xs text-muted-foreground mt-1">{description}</div>
      {footer}
    </div>
  );
}

// ======================== Type Settings ========================

export function TypeSettingsContent({
  kind,
  attribute,
  templateId,
  formulaContextKind,
  onUpdateAttributeType,
}: {
  kind: AttributeKindEnum;
  attribute: BaseTemplateAttribute;
  templateId: number;
  formulaContextKind: FormulaContextKind;
  onUpdateAttributeType: (updated: Record<string, unknown>) => void;
}) {
  switch (kind) {
    case AttributeKindEnum.SINGLE_NUMBER:
      return (
        <MeasurementFieldsEditor
          value={{
            measurement_default_unit: attribute.measurement_default_unit,
            measurement_instructions: attribute.measurement_instructions,
          }}
          onChange={(v) => onUpdateAttributeType(v as Record<string, unknown>)}
        />
      );

    case AttributeKindEnum.COMPUTED_NUMBER:
      return (
        <ComputedNumberSettings
          attribute={attribute}
          templateId={templateId}
          formulaContextKind={formulaContextKind}
          onUpdateAttributeType={onUpdateAttributeType}
        />
      );

    case AttributeKindEnum.INVENTORY_ITEM_REFERENCE:
      return (
        <InventoryFieldsEditor
          value={{
            inv_item_resource_id: attribute.inv_item_resource_id,
            inv_item_resource_category_id:
              attribute.inv_item_resource_category_id,
          }}
          onChange={(v) => onUpdateAttributeType(v as Record<string, unknown>)}
        />
      );

    case AttributeKindEnum.TEXT:
      return (
        <TextFieldsEditor
          value={{
            text_input_mode: attribute.text_input_mode,
            text_instructions: attribute.text_instructions,
            text_options: attribute.text_options,
          }}
          onChange={(v) => onUpdateAttributeType(v as Record<string, unknown>)}
        />
      );

    case AttributeKindEnum.MEDIA_ATTACHMENT:
      return (
        <div className="space-y-2">
          <Label className="text-xs font-medium">Media Type</Label>
          <Select
            value={(attribute.media_attachment_type as string) || ""}
            onValueChange={(value) =>
              onUpdateAttributeType({
                media_attachment_type: value as MediaAttachmentTypeEnum,
              })
            }
          >
            <SelectTrigger className="w-40">
              <SelectValue placeholder="Select type" />
            </SelectTrigger>
            <SelectContent>
              {Object.values(mediaAttachmentTypeEnum.enum).map((type) => (
                <SelectItem key={type} value={type}>
                  {capitalize(type.toLowerCase().replace("_", " "))}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      );

    case AttributeKindEnum.CHECK:
      return (
        <div className="text-xs text-muted-foreground">
          No additional settings for Check attributes.
        </div>
      );

    default:
      return null;
  }
}

// ======================== Computed Number Settings ========================

function ComputedNumberSettings({
  attribute,
  templateId,
  formulaContextKind,
  onUpdateAttributeType,
}: {
  attribute: BaseTemplateAttribute;
  templateId: number;
  formulaContextKind: FormulaContextKind;
  onUpdateAttributeType: (updated: Record<string, unknown>) => void;
}) {
  // Runtime shape is correct (kind paired with this editor's templateId);
  // the branded-id discrimination is type-only.
  const context = {
    kind: formulaContextKind,
    templateId,
    selfCode: (attribute.code as string | null) ?? undefined,
  } as FormulaContextInput;

  return (
    <div className="space-y-3">
      <div>
        <Label className="text-xs font-medium">Formula</Label>
        <div className="mt-1">
          <FormulaField
            value={(attribute.computed_number_formula as string) || ""}
            onSave={(next) =>
              onUpdateAttributeType({ computed_number_formula: next })
            }
            context={context}
            placeholder="e.g. upstream.wet_weight * 0.3"
            title="Attribute formula"
          />
        </div>
      </div>
      <div className="flex gap-4">
        <div className="space-y-1">
          <Label className="text-xs font-medium">Result Base Unit</Label>
          <UnitSelect
            value={(attribute.computed_number_result_base_unit as string) || ""}
            onChange={(next) =>
              onUpdateAttributeType({
                computed_number_result_base_unit: next,
                computed_number_result_display_unit: null,
              })
            }
            placeholder="Select base unit..."
            onlyBaseUnits
            className="h-6 text-xs"
          />
        </div>
        <div className="space-y-1">
          <Label className="text-xs font-medium">Result Display Unit</Label>
          <UnitSelect
            value={
              (attribute.computed_number_result_display_unit as string) || ""
            }
            onChange={(next) =>
              onUpdateAttributeType({
                computed_number_result_display_unit: next,
              })
            }
            allowedBaseUnits={
              attribute.computed_number_result_base_unit
                ? [attribute.computed_number_result_base_unit]
                : undefined
            }
            placeholder="Select display unit..."
            className="h-6 text-xs"
          />
        </div>
      </div>
      <FormulaUnitWarning
        formula={(attribute.computed_number_formula as string) || ""}
        declaredUnit={attribute.computed_number_result_base_unit as string | null}
        context={context}
      />
    </div>
  );
}

// ======================== Default Values ========================

export function DefaultValuesContent({
  kind,
  attribute,
  onUpdateSettings,
  unitLocked = false,
}: {
  kind?: AttributeKindEnum;
  attribute: BaseTemplateAttribute;
  onUpdateSettings: (settings: Record<string, unknown>) => void;
  /** The unit is fixed elsewhere (a generated field's blueprint): shown, not chosen. */
  unitLocked?: boolean;
}) {
  if (kind === AttributeKindEnum.SINGLE_NUMBER) {
    return (
      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label className="text-xs font-medium">Default Value</Label>
          <InlineEditableInput
            numeric
            value={attribute.default_value_number || ""}
            onSave={(value) =>
              onUpdateSettings({ default_value_number: value })
            }
          />
        </div>
        <div>
          <Label className="text-xs font-medium">Unit</Label>
          {unitLocked ? (
            <div className="mt-2 text-sm text-olive-11">
              {attribute.default_value_measurement_unit || "—"}
            </div>
          ) : (
            <UnitSelect
              value={attribute.default_value_measurement_unit || ""}
              onChange={(value) =>
                onUpdateSettings({ default_value_measurement_unit: value })
              }
            />
          )}
        </div>
      </div>
    );
  }

  if (kind === AttributeKindEnum.CHECK) {
    return (
      <div className="flex items-center space-x-2">
        <Checkbox
          id="default-check"
          checked={attribute.default_check_value ?? false}
          onCheckedChange={(checked) =>
            onUpdateSettings({ default_check_value: !!checked })
          }
        />
        <Label htmlFor="default-check" className="text-xs font-normal">
          Default to checked
        </Label>
      </div>
    );
  }

  return (
    <div className="text-xs text-muted-foreground">
      No parameter defaults for this attribute kind.
    </div>
  );
}
